import { Box } from '@mui/material';
import { alpha, Theme, useTheme } from '@mui/material/styles';
import mermaid from 'mermaid';
import React, { useEffect, useRef, useState } from 'react';

let mermaidInitializationSignature: string | null = null;

const buildMermaidConfig = (theme: Theme) => ({
  startOnLoad: false,
  securityLevel: 'strict' as const,
  theme: 'base' as const,
  themeVariables: {
    background: 'transparent',
    fontFamily: theme.typography.fontFamily,
    textColor: theme.palette.text.primary,
    lineColor: alpha(theme.palette.text.primary, 0.48),
    primaryColor: alpha(theme.palette.primary.light || theme.palette.primary.main, 0.26),
    primaryBorderColor: theme.palette.primary.main,
    primaryTextColor: theme.palette.text.primary,
    secondaryColor: alpha(theme.palette.warning.light || theme.palette.warning.main, 0.24),
    secondaryBorderColor: theme.palette.warning.main,
    secondaryTextColor: theme.palette.text.primary,
    tertiaryColor: alpha(theme.palette.secondary.light || theme.palette.secondary.main, 0.2),
    tertiaryBorderColor: theme.palette.secondary.main,
    tertiaryTextColor: theme.palette.text.primary,
    clusterBkg: alpha(theme.palette.secondary.main, 0.1),
    clusterBorder: alpha(theme.palette.secondary.main, 0.6),
    edgeLabelBackground: alpha(theme.palette.background.paper, 0.92),
    nodeBkg: alpha(theme.palette.primary.light || theme.palette.primary.main, 0.2),
    nodeBorder: theme.palette.primary.main,
    mainBkg: theme.palette.background.paper,
  },
});

function ensureMermaidInitialized(theme: Theme) {
  const config = buildMermaidConfig(theme);
  const nextSignature = JSON.stringify(config);

  if (mermaidInitializationSignature !== nextSignature) {
    mermaid.initialize(config);
    mermaidInitializationSignature = nextSignature;
  }
}

let idCounter = 0;

interface Props {
  chart: string;
}

const MermaidDiagram: React.FC<Props> = ({ chart }) => {
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const [svg, setSvg] = useState<string>('');
  const [error, setError] = useState<string>('');
  const idRef = useRef(`mermaid-${Date.now()}-${idCounter++}`);

  useEffect(() => {
    let cancelled = false;
    ensureMermaidInitialized(theme);

    mermaid
      .render(idRef.current, chart)
      .then(({ svg: renderedSvg }) => {
        if (!cancelled) {
          setSvg(renderedSvg);
          setError('');
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : String(err));
          setSvg('');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [chart, theme]);

  if (error) {
    return (
      <Box
        component="pre"
        sx={{ bgcolor: 'action.hover', p: 1, borderRadius: 1, overflow: 'auto', fontSize: '0.8rem' }}
      >
        <code>{chart}</code>
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      sx={{ my: 1, '& svg': { maxWidth: '100%', height: 'auto' } }}
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
};

export default MermaidDiagram;
